import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { leadListMappings } from '../ListColumnMapper/leadListMappings';

interface Props {
  open: boolean;
  onClose: () => void;
  file: File;
  fileHeaders: string[];
  mappings: Record<string, string>;
}

const parseCSVRow = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
};

const fieldLabelMap = Object.fromEntries(leadListMappings.map((m) => [m.value, m.name]));

export const FilePreviewDialog: FC<Props> = ({ open, onClose, file, fileHeaders, mappings }) => {
  const [allRows, setAllRows] = useState<string[][]>([]);
  const [displayedIndices, setDisplayedIndices] = useState<number[]>([]);

  useEffect(() => {
    if (!open) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result;
      if (!text || text instanceof ArrayBuffer) {
        return;
      }
      const lines = text.split(/\r?\n/).filter(Boolean);
      const parsed = lines.slice(1).map(parseCSVRow);
      setAllRows(parsed);
      setDisplayedIndices(Array.from({ length: Math.min(10, parsed.length) }, (_, i) => i));
    };
    reader.readAsText(file);
  }, [open, file]);

  const loadRandomRows = () => {
    const indices = new Set<number>();
    while (indices.size < 10) {
      indices.add(Math.floor(Math.random() * allRows.length));
    }
    setDisplayedIndices([...indices]);
  };

  const columns = fileHeaders
    .map((header, idx) => ({ header, systemField: mappings[header], idx }))
    .filter(({ systemField }) => !!systemField && systemField !== 'unmapped');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        File Preview
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Showing the first 10 rows using your column mappings. Data is not validated here.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <TableContainer sx={{ px: 2, pb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map(({ header, systemField }) => (
                  <TableCell key={header}>
                    <Typography variant="body2" fontWeight="bold">
                      {fieldLabelMap[systemField] ?? systemField}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {header}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedIndices.map((rowIdx) => (
                <TableRow key={rowIdx}>
                  {columns.map(({ header, idx }) => (
                    <TableCell key={header}>{allRows[rowIdx]?.[idx] ?? ''}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {allRows.length > 10 && (
          <Button onClick={loadRandomRows} variant="outlined">
            Load random rows
          </Button>
        )}
        <Button onClick={onClose} variant="contained" sx={{ ml: 'auto' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
